import { Menu } from 'lucide-react';
import type { ReactElement } from 'react';
import { useLocation } from 'react-router';
import { useAuthStore } from '@/entities/user';
import { useSignOut } from '@/features/auth';
import { Button } from '@/shared/ui';
import { currentMenuOf } from './current-menu';

export interface AdminHeaderProps {
  /** 모바일 GNB 드로어 열기 — 전달되면 md 미만에서 햄버거 버튼을 노출한다. */
  readonly onMobileMenuOpen?: () => void;
  /** 드로어 열림 상태 — 햄버거 버튼의 aria-expanded 에 반영된다. */
  readonly isMobileMenuOpen?: boolean;
  /** 드로어 요소 id — 열려 있는 동안 aria-controls 로 연결한다. */
  readonly mobileMenuId?: string;
}

/**
 * 관리자 앱 헤더 — 사이드바 옆 칸 맨 위의 얇은 흰 줄. 왼쪽에 지금 있는 메뉴 위치(분류 / 메뉴),
 * 오른쪽에 관리자 이름과 로그아웃. 로고와 주 메뉴(GNB)는 흑연 사이드바(widgets/admin-sidebar)가 담당한다.
 */
export const AdminHeader = ({
  onMobileMenuOpen,
  isMobileMenuOpen = false,
  mobileMenuId,
}: AdminHeaderProps): ReactElement => {
  const user = useAuthStore((state) => state.user);
  const { signOut } = useSignOut();
  const menu = currentMenuOf(useLocation().pathname);

  return (
    <header className="flex h-16 shrink-0 items-center justify-between gap-4 border-b border-foreground/10 bg-background px-6">
      <div className="flex min-w-0 items-center gap-3">
        {onMobileMenuOpen ? (
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="메뉴 열기"
            aria-expanded={isMobileMenuOpen}
            aria-controls={isMobileMenuOpen ? mobileMenuId : undefined}
            onClick={onMobileMenuOpen}
          >
            <Menu aria-hidden="true" />
          </Button>
        ) : null}

        {menu !== null ? (
          <p className="flex min-w-0 items-center gap-2 text-sm">
            <span className="truncate text-muted-foreground">{menu.section}</span>
            <span aria-hidden className="text-foreground/25">
              /
            </span>
            <span className="truncate font-semibold">{menu.label}</span>
          </p>
        ) : null}
      </div>

      {user ? (
        <div className="flex shrink-0 items-center gap-4">
          <span className="text-sm text-muted-foreground">{user.name}</span>
          <Button variant="outline" size="sm" className="px-4" onClick={() => void signOut()}>
            로그아웃
          </Button>
        </div>
      ) : null}
    </header>
  );
};
